/** 原创预设模板（约 20 款，七种折法均覆盖）。
 *
 * 所有图案在**扇区归一化坐标 (u, t)** 中定义：
 *   u ∈ [0,1] 沿扇区角度，t ∈ [0,1] 沿该方向的可用半径。
 * 因此同一套内容管线和参数可以适配七种折法，不依赖具体像素或纸张尺寸。
 *
 * 图案为几何花纹与少量爱心、蝴蝶、星形；不含"福""囍"等文字镂空。
 * 验证状态一律以「程序检查」起步，未做实物试剪前不得标为已验证。
 */
import type { CutShape, FinishedSize, FoldId, Polygon, Vec } from '../domain/types';
import { sectorMapper } from '../domain/folds/geometry';

export type PresetStatus = 'program-checked' | 'pending-physical' | 'physically-verified';

export interface Preset {
  id: string;
  name: string;
  foldId: FoldId;
  difficulty: 1 | 2 | 3 | 4 | 5;
  tags: string[];
  tip: string;
  status: PresetStatus;
  build: (foldId: FoldId, size: FinishedSize) => CutShape[];
}

type Ctx = {
  foldId: FoldId;
  size: FinishedSize;
  at: (u: number, t: number) => Vec;
};

const ctxOf = (foldId: FoldId, size: FinishedSize): Ctx => ({
  foldId,
  size,
  at: sectorMapper(foldId, size),
});

let counter = 0;
const nextId = (prefix: string): string => `${prefix}-${++counter}`;

/** 花瓣：沿 u 方向有宽度包络，t 从 t0 到 t1。 */
function petal(
  ctx: Ctx,
  u: number,
  t0: number,
  t1: number,
  halfWidthU: number,
  samples = 18,
  power = 0.75,
): Polygon {
  const right: Vec[] = [];
  const left: Vec[] = [];
  for (let i = 0; i <= samples; i++) {
    const s = i / samples;
    const t = t0 + (t1 - t0) * s;
    const w = halfWidthU * Math.pow(Math.sin(Math.PI * s), power);
    right.push(ctx.at(u + w, t));
    left.push(ctx.at(u - w, t));
  }
  return [...right, ...left.reverse()];
}

/** 贴在折线（u=0）上的半朵花瓣，反射后自动补成完整形状。 */
function halfPetalAtCrease(
  ctx: Ctx,
  t0: number,
  t1: number,
  halfWidthU: number,
  samples = 16,
): Polygon {
  const out: Vec[] = [];
  for (let i = 0; i <= samples; i++) {
    const s = i / samples;
    const t = t0 + (t1 - t0) * s;
    const w = halfWidthU * Math.pow(Math.sin(Math.PI * s), 0.8);
    out.push(ctx.at(w, t));
  }
  out.push(ctx.at(0, t1));
  out.push(ctx.at(0, t0));
  return out;
}

/** 纸边缘的三角缺口，展开后形成波浪边。 */
function edgeNotch(ctx: Ctx, u: number, halfWidthU: number, depthT = 0.12): Polygon {
  return [ctx.at(u - halfWidthU, 1 - depthT), ctx.at(u + halfWidthU, 1 - depthT), ctx.at(u, 1.0)];
}

/** 纸边缘的圆弧缺口（用折线近似）。 */
function edgeScallop(ctx: Ctx, u: number, halfWidthU: number, depthT = 0.14, samples = 14): Polygon {
  const out: Vec[] = [];
  for (let i = 0; i <= samples; i++) {
    const s = i / samples;
    const uu = u - halfWidthU + 2 * halfWidthU * s;
    const t = 1 - depthT * Math.sin(Math.PI * s);
    out.push(ctx.at(uu, t));
  }
  return out;
}

function diamond(ctx: Ctx, u: number, t: number, halfWidthU: number, halfHeightT: number): Polygon {
  return [ctx.at(u, t - halfHeightT), ctx.at(u + halfWidthU, t), ctx.at(u, t + halfHeightT), ctx.at(u - halfWidthU, t)];
}

function circleCut(ctx: Ctx, u: number, t: number, radiusMm: number): CutShape {
  return { id: nextId('c'), kind: 'circle', center: ctx.at(u, t), radius: radiusMm };
}

const polyCut = (points: Polygon): CutShape => ({ id: nextId('p'), kind: 'polygon', points });

// ---------------------------------------------------------------- 预设定义

const presets: Preset[] = [
  // ---- 对折 3 款
  {
    id: 'half-heart',
    name: '一心相印',
    foldId: 'half',
    difficulty: 1,
    tags: ['爱心', '入门'],
    tip: '沿中线折叠后剪出半个心形，展开即为一颗完整的心。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      return [polyCut(halfPetalAtCrease(ctx, 0.28, 0.78, 0.17, 24))];
    },
  },
  {
    id: 'half-butterfly',
    name: '蝶影',
    foldId: 'half',
    difficulty: 2,
    tags: ['蝴蝶', '动物'],
    tip: '上下两片翅膀由中线连接，注意保留 2 mm 以上的身体连接。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const w = 0.2;
      return [
        polyCut(petal(ctx, 0.34, 0.3, 0.72, w, 18, 0.9)),
        polyCut(petal(ctx, 0.34, 0.74, 0.92, w * 0.62, 14, 0.9)),
        circleCut(ctx, 0.34, 0.86, 2.2),
        circleCut(ctx, 0.28, 0.5, 1.6),
        circleCut(ctx, 0.4, 0.58, 1.2),
      ];
    },
  },
  {
    id: 'half-leaf',
    name: '叶脉',
    foldId: 'half',
    difficulty: 1,
    tags: ['植物', '入门'],
    tip: '主叶脉沿折线保留，叶肉部分剪空后展开为一片对称叶子。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.18, 0.86, 0.22, 20, 0.6))];
      for (let i = 0; i < 4; i++) {
        const t = 0.3 + i * 0.13;
        cuts.push(circleCut(ctx, 0.5, t, 1.4));
      }
      return cuts;
    },
  },

  // ---- 四等分折 3 款
  {
    id: 'quarter-clover',
    name: '四叶草',
    foldId: 'quarter',
    difficulty: 1,
    tags: ['植物', '四向'],
    tip: '在四分之一区域内剪一片叶子，展开后四向对称。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      return [polyCut(petal(ctx, 0.5, 0.16, 0.72, 0.26, 20, 0.7)), circleCut(ctx, 0.5, 0.8, 2.4)];
    },
  },
  {
    id: 'quarter-square',
    name: '方胜团花',
    foldId: 'quarter',
    difficulty: 2,
    tags: ['几何', '四向'],
    tip: '方胜纹靠直角对齐，剪口不要贴到纸边，留出连接条。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(diamond(ctx, 0.5, 0.42, 0.22, 0.16))];
      cuts.push(polyCut(diamond(ctx, 0.5, 0.72, 0.14, 0.1)));
      cuts.push(circleCut(ctx, 0.5, 0.22, 2.6));
      return cuts;
    },
  },
  {
    id: 'quarter-lattice',
    name: '菱格窗花',
    foldId: 'quarter',
    difficulty: 2,
    tags: ['几何', '边框'],
    tip: '边缘小三角会让展开后的外框呈锯齿状，注意不要切断外框。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(diamond(ctx, 0.5, 0.5, 0.18, 0.14))];
      cuts.push(polyCut(edgeNotch(ctx, 0.35, 0.08, 0.1)));
      cuts.push(polyCut(edgeNotch(ctx, 0.65, 0.08, 0.1)));
      return cuts;
    },
  },

  // ---- 四角放射折 3 款
  {
    id: 'radial45-window',
    name: '四角窗花',
    foldId: 'radial45',
    difficulty: 2,
    tags: ['窗花', '经典'],
    tip: '经典四角窗花，中心留实心圆避免整体散架。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.2, 0.62, 0.28, 18, 0.8))];
      cuts.push(polyCut(petal(ctx, 0.5, 0.68, 0.9, 0.18, 14, 0.8)));
      cuts.push(polyCut(edgeScallop(ctx, 0.5, 0.2, 0.1)));
      return cuts;
    },
  },
  {
    id: 'radial45-swirl',
    name: '四叶旋纹',
    foldId: 'radial45',
    difficulty: 3,
    tags: ['几何', '动态'],
    tip: '斜向花瓣会产生旋转感，展开后四片错落。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      for (let i = 0; i < 3; i++) {
        const t = 0.26 + i * 0.2;
        cuts.push(polyCut(petal(ctx, 0.34 + i * 0.06, t, t + 0.14, 0.14, 14, 0.9)));
      }
      cuts.push(circleCut(ctx, 0.72, 0.8, 2.2));
      return cuts;
    },
  },
  {
    id: 'radial45-border',
    name: '角花边',
    foldId: 'radial45',
    difficulty: 2,
    tags: ['边框', '四向'],
    tip: '以边框为主的图案，中心区域保持完整，适合做底垫。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(edgeScallop(ctx, 0.5, 0.26, 0.12))];
      cuts.push(polyCut(edgeNotch(ctx, 0.12, 0.05, 0.08)));
      cuts.push(polyCut(edgeNotch(ctx, 0.88, 0.05, 0.08)));
      cuts.push(polyCut(diamond(ctx, 0.5, 0.55, 0.16, 0.12)));
      return cuts;
    },
  },

  // ---- 五角放射折 3 款
  {
    id: 'radial36-star',
    name: '五角星',
    foldId: 'radial36',
    difficulty: 3,
    tags: ['星形', '五向'],
    tip: '五角星靠尖角成形，尖端要接近纸边但不能越过。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      cuts.push(polyCut(petal(ctx, 0.5, 0.55, 0.96, 0.2, 16, 1.4)));
      cuts.push(polyCut(petal(ctx, 0.5, 0.18, 0.42, 0.26, 14, 0.7)));
      return cuts;
    },
  },
  {
    id: 'radial36-plum',
    name: '五瓣梅',
    foldId: 'radial36',
    difficulty: 3,
    tags: ['植物', '五向'],
    tip: '五瓣梅的花瓣较圆，中心留出花蕊连接。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.28, 0.72, 0.34, 20, 0.6))];
      for (let i = 0; i < 3; i++) cuts.push(circleCut(ctx, 0.5, 0.5, 1.2 + i * 0.6));
      cuts.push(polyCut(edgeScallop(ctx, 0.5, 0.22, 0.09)));
      return cuts;
    },
  },
  {
    id: 'radial36-ray',
    name: '星芒边饰',
    foldId: 'radial36',
    difficulty: 4,
    tags: ['边框', '五向'],
    tip: '细长芒线需要较薄的纸；24 层以内的折叠请保持芒线宽度。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      for (let i = 0; i < 3; i++) {
        const u = 0.22 + i * 0.28;
        cuts.push(polyCut(petal(ctx, u, 0.62, 0.95, 0.06, 10, 1.2)));
      }
      cuts.push(circleCut(ctx, 0.5, 0.35, 3));
      return cuts;
    },
  },

  // ---- 六角放射折 3 款
  {
    id: 'radial30-snowflake',
    name: '雪花',
    foldId: 'radial30',
    difficulty: 3,
    tags: ['雪花', '六向', '经典'],
    tip: '六角雪花的关键是主干保持连通，侧枝可以剪得细一些。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      // 主干两侧的长条
      cuts.push(polyCut(petal(ctx, 0.16, 0.3, 0.9, 0.05, 10, 1.1)));
      cuts.push(polyCut(petal(ctx, 0.84, 0.3, 0.9, 0.05, 10, 1.1)));
      // 侧枝
      for (let i = 0; i < 3; i++) {
        const t = 0.42 + i * 0.16;
        cuts.push(polyCut(petal(ctx, 0.5, t, t + 0.1, 0.1, 10, 1.2)));
      }
      cuts.push(circleCut(ctx, 0.5, 0.16, 2.4));
      return cuts;
    },
  },
  {
    id: 'radial30-chrysanthemum',
    name: '六瓣菊',
    foldId: 'radial30',
    difficulty: 2,
    tags: ['植物', '六向'],
    tip: '六片花瓣互相不接触，中心花心保留。',
    status: 'program-checked',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.26, 0.78, 0.3, 18, 0.55))];
      cuts.push(circleCut(ctx, 0.5, 0.86, 2.2));
      cuts.push(polyCut(edgeScallop(ctx, 0.5, 0.24, 0.1)));
      return cuts;
    },
  },
  {
    id: 'radial30-ice',
    name: '冰晶纹',
    foldId: 'radial30',
    difficulty: 4,
    tags: ['雪花', '细密'],
    tip: '细密纹样建议使用 60–80 g/m² 纸，并先试剪最小的那个剪口。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      for (let i = 0; i < 4; i++) {
        const t = 0.24 + i * 0.17;
        cuts.push(polyCut(diamond(ctx, 0.5, t, 0.1, 0.055)));
      }
      cuts.push(polyCut(petal(ctx, 0.12, 0.5, 0.92, 0.045, 8, 1.0)));
      cuts.push(polyCut(petal(ctx, 0.88, 0.5, 0.92, 0.045, 8, 1.0)));
      return cuts;
    },
  },

  // ---- 八角放射折 3 款
  {
    id: 'radial225-lotus',
    name: '八瓣莲',
    foldId: 'radial22_5',
    difficulty: 4,
    tags: ['植物', '八向'],
    tip: '十六层已明显变厚，剪口宽度不要低于 4 mm。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.3, 0.8, 0.32, 18, 0.55))];
      cuts.push(circleCut(ctx, 0.5, 0.17, 3));
      cuts.push(polyCut(edgeScallop(ctx, 0.5, 0.3, 0.1)));
      return cuts;
    },
  },
  {
    id: 'radial225-lace',
    name: '蕾丝边',
    foldId: 'radial22_5',
    difficulty: 4,
    tags: ['边框', '细密'],
    tip: '蕾丝效果靠密集小孔，建议先剪一个小孔确认纸能承受。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      for (let i = 0; i < 3; i++) {
        cuts.push(polyCut(edgeScallop(ctx, 0.28 + i * 0.22, 0.09, 0.1, 10)));
      }
      for (let i = 0; i < 3; i++) {
        cuts.push(circleCut(ctx, 0.5, 0.34 + i * 0.14, 1.8));
      }
      return cuts;
    },
  },
  {
    id: 'radial225-rosette',
    name: '八角团花',
    foldId: 'radial22_5',
    difficulty: 3,
    tags: ['窗花', '八向'],
    tip: '团花讲究中心与边饰的呼应，外围切得比中心细一些更耐看。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [polyCut(petal(ctx, 0.5, 0.2, 0.5, 0.3, 16, 0.7))];
      for (let i = 0; i < 2; i++) {
        cuts.push(polyCut(diamond(ctx, 0.5, 0.6 + i * 0.16, 0.14 - i * 0.04, 0.08)));
      }
      cuts.push(polyCut(edgeNotch(ctx, 0.5, 0.08, 0.09)));
      return cuts;
    },
  },

  // ---- 十二角放射折 2 款
  {
    id: 'radial15-snow',
    name: '细密雪花',
    foldId: 'radial15',
    difficulty: 5,
    tags: ['雪花', '十二向', '高阶'],
    tip: '二十四层很厚，务必用薄纸；先用废纸练习折叠角度。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      cuts.push(polyCut(petal(ctx, 0.5, 0.28, 0.86, 0.1, 12, 1.0)));
      for (let i = 0; i < 3; i++) {
        cuts.push(polyCut(diamond(ctx, 0.5, 0.34 + i * 0.18, 0.07, 0.05)));
      }
      cuts.push(circleCut(ctx, 0.5, 0.14, 2));
      return cuts;
    },
  },
  {
    id: 'radial15-ray',
    name: '放射菊',
    foldId: 'radial15',
    difficulty: 5,
    tags: ['植物', '十二向', '高阶'],
    tip: '细长芒线在 24 层下容易断，建议先把芒线剪到 5 mm 以上宽度。',
    status: 'pending-physical',
    build: (f, s) => {
      const ctx = ctxOf(f, s);
      const cuts: CutShape[] = [];
      for (let i = 0; i < 2; i++) {
        const u = 0.3 + i * 0.4;
        cuts.push(polyCut(petal(ctx, u, 0.5, 0.95, 0.07, 10, 1.3)));
      }
      cuts.push(polyCut(petal(ctx, 0.5, 0.22, 0.46, 0.24, 14, 0.7)));
      return cuts;
    },
  },
];

export const PRESETS: Preset[] = presets;

export function presetById(id: string): Preset | undefined {
  return PRESETS.find((p) => p.id === id);
}

export function presetsForFold(foldId: FoldId): Preset[] {
  return PRESETS.filter((p) => p.foldId === foldId);
}

/** 按预设生成具体剪口（会重置 id 计数器，保证可重复构建）。 */
export function buildPresetCuts(preset: Preset, size: FinishedSize): CutShape[] {
  counter = 0;
  return preset.build(preset.foldId, size);
}

export function presetCountByFold(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of PRESETS) out[p.foldId] = (out[p.foldId] ?? 0) + 1;
  return out;
}